import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import {
    useGetAllRequestCountQuery,
    useLazyGetDraftListQuery,
    useLazyGetPendingApprovalListQuery,
    useLazyGetApprovedListQuery,
    useLazyGetRejectedListQuery,
    useLazyGetAllOrderRequestsQuery
} from '../../../../api/endpoints/vehicleOrder';

const TAB_KEYS = ['draft', 'pending', 'approved', 'rejected', 'all'];

const useOrderRequests = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const {
        data: rawStatusCounts = {},
        refetch: refetchStatusCounts
    } = useGetAllRequestCountQuery();

    const [fetchDraft, { data: draftData, isFetching: draftLoading }] = useLazyGetDraftListQuery();
    const [fetchPending, { data: pendingData, isFetching: pendingLoading }] = useLazyGetPendingApprovalListQuery();
    const [fetchApproved, { data: approvedData, isFetching: approvedLoading }] = useLazyGetApprovedListQuery();
    const [fetchRejected, { data: rejectedData, isFetching: rejectedLoading }] = useLazyGetRejectedListQuery();
    const [fetchAll, { data: allData, isFetching: allLoading }] = useLazyGetAllOrderRequestsQuery();

    const responseMap = [draftData, pendingData, approvedData, rejectedData, allData];
    const loadingMap = [draftLoading, pendingLoading, approvedLoading, rejectedLoading, allLoading];
    const activeResponse = responseMap[activeTabIndex];
    const statusCounts = {
        draft: rawStatusCounts.draftCount,
        pending: rawStatusCounts.pendingApprovalCount,
        approved: rawStatusCounts.approvedCount,
        rejected: rawStatusCounts.rejectedCount,
        all: rawStatusCounts.allCount
    };

    const refetchActiveList = useCallback(() => {
        const fetchFns = [fetchDraft, fetchPending, fetchApproved, fetchRejected, fetchAll];
        return fetchFns[activeTabIndex]({ languageId });
    }, [activeTabIndex, languageId, fetchDraft, fetchPending, fetchApproved, fetchRejected, fetchAll]);

    useEffect(() => {
        refetchActiveList();
    }, [refetchActiveList]);

    const refetch = useCallback(() => {
        return Promise.all([refetchActiveList(), refetchStatusCounts()]);
    }, [refetchActiveList, refetchStatusCounts]);

    const tabConfig = [
        { key: TAB_KEYS[0], label: th('vehicleSalesOrderRequests.tabs.draft'), icon: 'pi pi-file-edit', color: 'orange' },
        { key: TAB_KEYS[1], label: th('vehicleSalesOrderRequests.tabs.pendingApproval'), icon: 'pi pi-clock', color: 'blue' },
        { key: TAB_KEYS[2], label: th('vehicleSalesOrderRequests.tabs.approved'), icon: 'pi pi-check-circle', color: 'green' },
        { key: TAB_KEYS[3], label: th('vehicleSalesOrderRequests.tabs.rejected'), icon: 'pi pi-times-circle', color: 'red' },
        { key: TAB_KEYS[4], label: th('vehicleSalesOrderRequests.tabs.allRequests'), icon: 'pi pi-list', color: 'gray' }
    ];

    return {
        th,
        activeTabIndex,
        setActiveTabIndex,
        activeTabKey: TAB_KEYS[activeTabIndex],
        tabConfig,
        orders: activeResponse?.items ?? [],
        statusCounts,
        isLoading: loadingMap[activeTabIndex],
        refetch
    };
};

export default useOrderRequests;
